import React from 'react';
import { Box, Grid, Button, Typography } from '@mui/material';
import { ExceptionPeriod } from 'common/types';
import DatePicker from 'components/DatePicker';

import OpeningHours from './OpeningHours';
import useShopFormat from 'hooks/useShopFormat';
import { useTranslation } from 'services/localization';
import moment from 'moment-timezone';
import { isMainOpeningHours } from './utils';

const isMobile = window.matchMedia('screen and (orientation:portrait) and (max-width: 640px)')
	.matches;

interface Props {
	value: ExceptionPeriod;
	onChange: (value: ExceptionPeriod) => void;
	onDelete: () => void;
	isValid?: boolean;
}
const SingleExceptionPeriod = (props: Props) => {
	const { localFormat } = useShopFormat();
	const { t } = useTranslation();
	const { value, onChange, onDelete, isValid } = props;

	const handleStartDateChange = (selectedStartDate: moment.Moment | null) => {
		if (!selectedStartDate) {
			return;
		}
		if (selectedStartDate.isAfter(value.endDate, 'day')) {
			const newEndDate = moment(selectedStartDate).add(
				moment(value.endDate).diff(value.startDate, 'day'),
				'day',
			);
			onChange({
				...value,
				startDate: selectedStartDate.format('YYYY-MM-DD'),
				endDate: newEndDate.format('YYYY-MM-DD'),
			});
		} else {
			onChange({
				...value,
				startDate: selectedStartDate.format('YYYY-MM-DD'),
			});
		}
	};

	const handleEndDateChange = (selectedEndDate: moment.Moment | null) => {
		if (!selectedEndDate) {
			return;
		}

		onChange({
			...value,
			endDate: selectedEndDate.format('YYYY-MM-DD'),
		});
	};

	return (
		<Box>
			<Box
				mb={3}
				display="flex"
				flexDirection="row"
				justifyContent="space-between"
				alignItems="center"
			>
				<Box mr={2}>
					<Typography variant="h6">
						{t('common:dates.dateRange', {
							startDate: localFormat(moment(value.startDate), 'DD.MM.YYYY'),
							endDate: localFormat(moment(value.endDate), 'DD.MM.YYYY'),
							defaultValue: 'From {{startDate}} to {{endDate}}',
						})}
					</Typography>
					{!isValid && (
						<Typography variant="body2" color="error">
							{t(
								'openingHours.exceptionPeriodOverlaps',
								'This exception period overlaps with another exception period',
							)}
						</Typography>
					)}
				</Box>
				<Button color="secondary" variant="outlined" onClick={onDelete}>
					{t('common:actions.delete')}
				</Button>
			</Box>
			<Grid container spacing={2}>
				<Grid item xs={12} sm={7}>
					<Typography variant="body2" display="inline">
						{t('common:times.startsAt')}
					</Typography>
					<DatePicker
						value={value.startDate}
						onChange={handleStartDateChange}
						id="startDate"
						styleProps={{
							height: 35,
							bordered: true,
						}}
						singleDatePickerProps={{
							showDefaultInputIcon: !isMobile,
							isOutsideRange: (day: moment.Moment) => moment(day).isBefore(moment(), 'day'),
						}}
					/>
				</Grid>
				<Grid item xs={12} sm={7}>
					<Typography variant="body2" display="inline">
						{t('common:times.endsAt')}
					</Typography>
					<DatePicker
						value={value.endDate}
						onChange={handleEndDateChange}
						id="endDate"
						styleProps={{
							height: 35,
							bordered: true,
						}}
						singleDatePickerProps={{
							showDefaultInputIcon: !isMobile,
							isOutsideRange: (day: moment.Moment) =>
								moment(day).isBefore(moment(value.startDate), 'day'),
						}}
					/>
				</Grid>
			</Grid>
			<OpeningHours
				value={value.openingHours}
				onChange={(data) => {
					const openingHours = isMainOpeningHours(data)
						? data
						: { ...data, openingHoursWithLocation: {} };
					onChange({ ...value, openingHours });
				}}
			/>
		</Box>
	);
};

export default SingleExceptionPeriod;
